import {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    ReactNode,
} from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

export interface User {
    id: string;
    email: string;
    name?: string;
}

export interface Tenant {
    id: string;
    name: string;
    plan: 'free' | 'pro' | 'enterprise';
    classify_quota: number;
    classify_used: number;
    role: 'owner' | 'admin' | 'member' | 'viewer';
}

interface AuthState {
    user: User | null;
    tenant: Tenant | null;
    token: string | null;
    loading: boolean;
}

interface AuthContextValue extends AuthState {
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, name?: string, company?: string) => Promise<void>;
    logout: () => void;
    updateTenant: (updates: Partial<Pick<Tenant, 'name'>>) => void;
    isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = 'hsc_token';

export function AuthProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({
        user: null,
        tenant: null,
        token: null,
        loading: true,
    });

    // On mount: restore token from localStorage and verify
    useEffect(() => {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        if (!savedToken) {
            setState((s) => ({ ...s, loading: false }));
            return;
        }
        api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
        api.get('/auth/me')
            .then((res) => {
                setState({ user: res.data.user, tenant: res.data.tenant, token: savedToken, loading: false });
            })
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                delete api.defaults.headers.common['Authorization'];
                setState({ user: null, tenant: null, token: null, loading: false });
            });
    }, []);

    const setSession = (token: string, user: User, tenant: Tenant) => {
        localStorage.setItem(TOKEN_KEY, token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setState({ user, tenant, token, loading: false });
    };

    const login = useCallback(async (email: string, password: string) => {
        const res = await api.post('/auth/login', { email, password });
        setSession(res.data.token, res.data.user, res.data.tenant);
        toast.success(`欢迎回来，${res.data.user.name || res.data.user.email}！`);
    }, []);

    const register = useCallback(async (
        email: string,
        password: string,
        name?: string,
        company?: string,
    ) => {
        const res = await api.post('/auth/register', { email, password, name, company });
        setSession(res.data.token, res.data.user, res.data.tenant);
        toast.success(`注册成功，工作空间「${res.data.tenant.name}」已创建！`);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        delete api.defaults.headers.common['Authorization'];
        setState({ user: null, tenant: null, token: null, loading: false });
        toast.success('已安全退出登录');
    }, []);

    // Optimistic local update (e.g. after renaming workspace)
    const updateTenant = useCallback((updates: Partial<Pick<Tenant, 'name'>>) => {
        setState((s) => s.tenant
            ? { ...s, tenant: { ...s.tenant, ...updates } }
            : s
        );
    }, []);

    return (
        <AuthContext.Provider
            value={{
                ...state,
                login,
                register,
                logout,
                updateTenant,
                isAuthenticated: !!state.user,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
}
